import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Discreet page-level cache telemetry badge. Polls
 * `/api/research-cache/telemetry` every `pollIntervalMs` (default 45s)
 * and renders a compact summary so the operator can confirm the disk
 * cache is actually being hit and saving time.
 *
 * Pure UX. ZERO impact on backtest/strategy/live trading code paths.
 * Degrades silently if the endpoint fails (renders nothing).
 */
export function CacheTelemetryBadge({ testidPrefix = "bt", pollIntervalMs = 45000 }) {
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const tid = (k) => `${testidPrefix}-cache-telemetry-${k}`;

  useEffect(() => {
    mountedRef.current = true;
    const tick = async () => {
      try {
        const d = await api.researchCacheTelemetry();
        if (mountedRef.current) {
          setT(d);
          setErr(false);
        }
      } catch {
        if (mountedRef.current) setErr(true);
      }
    };
    tick();
    timerRef.current = setInterval(tick, pollIntervalMs);
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [pollIntervalMs]);

  if (err && !t) return null;            // graceful: endpoint unavailable
  if (!t) return null;                   // first load — nothing yet

  const disk = t.disk || {};
  const mem  = t.memory || {};
  const memHits   = mem.hits ?? 0;
  const memMisses = mem.misses ?? 0;
  const memTotal  = memHits + memMisses;
  const memRatio  = memTotal > 0 ? (memHits / memTotal) * 100 : 0;
  const diskRatio = (disk.kline_hit_ratio ?? 0) * 100;
  const savedS    = Math.round((disk.estimated_time_saved_ms ?? 0) / 1000);
  const files     = disk.cache_files ?? 0;
  const mb        = disk.cache_size_mb ?? 0;

  const tooltip = [
    `Memory ${memHits}h / ${memMisses}m (${memRatio.toFixed(0)}%)`,
    `Disk ${disk.disk_kline_hits ?? 0}h / ${disk.disk_kline_misses ?? 0}m (${diskRatio.toFixed(0)}%)`,
    `Files ${files} · ${mb} MB`,
    savedS > 0 ? `Saved ≈ ${savedS}s` : null,
    `Schema v${disk.schema_version ?? "?"} · indicators v${disk.indicator_version ?? "?"}`,
    disk.has_parquet === false ? "(Parquet unavailable — JSON gzip fallback)" : null,
  ].filter(Boolean).join("\n");

  return (
    <div
      className="inline-flex items-center gap-2 text-[10px] font-mono text-[#71717A] border border-[#52525B]/30 bg-[#0F0F14] rounded-sm px-2 py-1"
      data-testid={tid("wrapper")}
      title={tooltip}
    >
      <span className="uppercase tracking-widest text-[#52525B]">cache</span>
      <span data-testid={tid("disk-ratio")}>
        disk {diskRatio.toFixed(0)}%
      </span>
      <span className="text-[#3F3F46]">·</span>
      <span data-testid={tid("mem-ratio")}>
        mem {memRatio.toFixed(0)}%
      </span>
      <span className="text-[#3F3F46]">·</span>
      <span data-testid={tid("files")}>
        {files}f / {mb}MB
      </span>
      {savedS > 0 ? (
        <>
          <span className="text-[#3F3F46]">·</span>
          <span data-testid={tid("saved")} className="text-[#00E676]/80">
            saved ~{savedS}s
          </span>
        </>
      ) : null}
    </div>
  );
}

export default CacheTelemetryBadge;
